import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { KoStage } from '@punditxi/shared';
import { BracketTree } from '../components/BracketTree';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { GoalscorerSearch } from '../components/GoalscorerSearch';
import { GroupCard, useGroupOpenState, type GroupPicks } from '../components/GroupCard';
import { api } from '../lib/api';
import { haptic } from '../lib/telegram';
import { cn } from '../lib/utils';

type Stage = 'groups' | 'ko' | 'final';

export function BracketBuilder() {
  const [params, setParams] = useSearchParams();
  const stage = (params.get('stage') ?? 'groups') as Stage;
  const setStage = (s: Stage) => {
    haptic.select();
    setParams({ stage: s });
  };

  return (
    <div className="mx-auto max-w-md px-4 pt-4 pb-32 space-y-4">
      <StageHeader stage={stage} setStage={setStage} />
      {stage === 'groups' && <GroupsStage onNext={() => setStage('ko')} />}
      {stage === 'ko' && <KoStageView onNext={() => setStage('final')} />}
      {stage === 'final' && <FinalStage />}
    </div>
  );
}

function StageHeader({ stage, setStage }: { stage: Stage; setStage: (s: Stage) => void }) {
  const step = stage === 'groups' ? 1 : stage === 'ko' ? 2 : 3;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 font-display-alt text-xs uppercase tracking-widest text-text-faint">
        <Link to="/bracket" className="text-text-dim">← Bracket</Link>
        <span>·</span>
        <span>Step {step}/3</span>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {(['groups', 'ko', 'final'] as Stage[]).map((s, i) => (
          <button
            key={s}
            onClick={() => setStage(s)}
            className={cn(
              'rounded-md py-2 font-display-alt text-[10px] uppercase tracking-widest border',
              stage === s
                ? 'bg-trophy-gold text-bg-night border-trophy-gold'
                : 'bg-bg-card text-text-dim border-border-base',
            )}
          >
            {i + 1}. {s === 'groups' ? 'Groups' : s === 'ko' ? 'Knockouts' : 'Final'}
          </button>
        ))}
      </div>
    </div>
  );
}

// --- Groups stage ----------------------------------------------------------

function GroupsStage({ onNext }: { onNext: () => void }) {
  const groups = useQuery({ queryKey: ['wc', 'groups'], queryFn: () => api.wcGroups() });
  const bracket = useQuery({ queryKey: ['bracket', 'me'], queryFn: () => api.bracketMe() });
  const qc = useQueryClient();
  const { isOpen, toggle } = useGroupOpenState({ initialOpen: 'A' });
  const locked = !!bracket.data?.entry?.isLocked;

  // Local picks state, seeded from server data
  const [picks, setPicks] = useState<Record<string, GroupPicks>>({});
  useEffect(() => {
    if (!bracket.data) return;
    const next: Record<string, GroupPicks> = {};
    for (const gp of bracket.data.groupPicks) {
      const cur = next[gp.groupLabel] ?? { first: null, second: null, third: null };
      if (gp.position === 1) cur.first = gp.teamId;
      else if (gp.position === 2) cur.second = gp.teamId;
      else if (gp.position === 3) cur.third = gp.teamId;
      next[gp.groupLabel] = cur;
    }
    setPicks(next);
  }, [bracket.data]);

  const submit = useMutation({
    mutationFn: () => {
      const groupPicks = Object.entries(picks).flatMap(([label, p]) => {
        const out: { groupLabel: string; position: 1 | 2 | 3; teamId: number }[] = [];
        if (p.first) out.push({ groupLabel: label, position: 1, teamId: p.first });
        if (p.second) out.push({ groupLabel: label, position: 2, teamId: p.second });
        if (p.third) out.push({ groupLabel: label, position: 3, teamId: p.third });
        return out;
      });
      return api.bracketSubmitPicks({ groupPicks });
    },
    onSuccess: async () => {
      haptic.success();
      await qc.invalidateQueries({ queryKey: ['bracket', 'me'] });
      onNext();
    },
    onError: () => haptic.error(),
  });

  const allComplete = useMemo(() => {
    const groupLabels = groups.data?.groups.map((g) => g.groupLabel) ?? [];
    if (groupLabels.length === 0) return false;
    return groupLabels.every((l) => {
      const p = picks[l];
      return p && p.first !== null && p.second !== null && p.third !== null;
    });
  }, [picks, groups.data]);

  if (groups.isLoading) {
    return <Skeleton />;
  }

  if (!groups.data || groups.data.groups.length === 0) {
    return (
      <Card>
        <div className="text-text-dim text-sm">
          Group draw not loaded yet. The seed runs daily; check back tomorrow.
        </div>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-2">
        {groups.data.groups.map((g) => (
          <GroupCard
            key={g.groupLabel}
            label={g.groupLabel}
            teams={g.teams.map((t) => ({ teamId: t.teamId, name: t.name }))}
            picks={picks[g.groupLabel] ?? { first: null, second: null, third: null }}
            open={isOpen(g.groupLabel)}
            onToggle={() => toggle(g.groupLabel)}
            onChange={(next) => setPicks((p) => ({ ...p, [g.groupLabel]: next }))}
            locked={locked}
          />
        ))}
      </div>

      <div className="fixed bottom-16 inset-x-0 z-20 bg-gradient-to-t from-bg-night via-bg-night to-transparent pt-6 pb-3 px-4">
        <div className="mx-auto max-w-md">
          <Button
            variant="gold"
            fullWidth
            disabled={!allComplete || submit.isPending || locked}
            onClick={() => submit.mutate()}
          >
            {submit.isPending ? 'Saving…' : allComplete ? 'Save & continue →' : `${countDone(picks)}/${groups.data.groups.length * 3} picked`}
          </Button>
        </div>
      </div>
    </>
  );
}

function countDone(picks: Record<string, GroupPicks>): number {
  let n = 0;
  for (const p of Object.values(picks)) {
    if (p.first) n++;
    if (p.second) n++;
    if (p.third) n++;
  }
  return n;
}

// --- Knockout stage --------------------------------------------------------

const KO_STAGES_ORDER: KoStage[] = ['R32', 'R16', 'QF', 'SF'];

function KoStageView({ onNext }: { onNext: () => void }) {
  const bracket = useQuery({ queryKey: ['bracket', 'me'], queryFn: () => api.bracketMe() });
  const groups = useQuery({ queryKey: ['wc', 'groups'], queryFn: () => api.wcGroups() });
  const qc = useQueryClient();

  const locked = !!bracket.data?.entry?.isLocked;
  const [picks, setPicks] = useState<Record<string, number | null>>({});

  useEffect(() => {
    if (!bracket.data) return;
    const next: Record<string, number | null> = {};
    for (const p of bracket.data.koPicks) next[`${p.stage}:${p.slotId}`] = p.teamId;
    setPicks(next);
  }, [bracket.data]);

  const submit = useMutation({
    mutationFn: () => {
      const koPicks = Object.entries(picks).map(([key, teamId]) => {
        const [stage, slotId] = key.split(':') as [KoStage, string];
        return { stage, slotId, teamId: teamId ?? null };
      });
      return api.bracketSubmitPicks({ koPicks });
    },
    onSuccess: async () => {
      haptic.success();
      await qc.invalidateQueries({ queryKey: ['bracket', 'me'] });
      onNext();
    },
    onError: () => haptic.error(),
  });

  const allTeams = useMemo(
    () =>
      (groups.data?.groups ?? []).flatMap((g) =>
        g.teams.map((t) => ({ id: t.teamId, name: t.name, groupLabel: g.groupLabel })),
      ),
    [groups.data],
  );

  const picksList = useMemo(
    () =>
      Object.entries(picks).map(([key, teamId]) => {
        const [stage, slotId] = key.split(':') as [KoStage, string];
        return { stage, slotId, teamId };
      }),
    [picks],
  );

  function handleSlotChange(stage: KoStage, slotId: string, teamId: number | null) {
    setPicks((p) => ({ ...p, [`${stage}:${slotId}`]: teamId }));
  }

  return (
    <>
      <BracketTree
        teams={allTeams}
        picks={picksList}
        onChange={handleSlotChange}
        locked={locked}
      />

      <Card className="mt-4">
        <div className="text-xs text-text-dim leading-relaxed">
          <strong className="text-text-base">How picking works:</strong> R32 lets you pick from any
          of the 32 advancing teams. R16 onwards inherits from your prior round picks. Drag the tree
          horizontally to see all five rounds.
        </div>
      </Card>

      <div className="fixed bottom-16 inset-x-0 z-20 bg-gradient-to-t from-bg-night via-bg-night to-transparent pt-6 pb-3 px-4">
        <div className="mx-auto max-w-md">
          <Button variant="gold" fullWidth disabled={submit.isPending || locked} onClick={() => submit.mutate()}>
            {submit.isPending ? 'Saving…' : 'Save & continue →'}
          </Button>
        </div>
      </div>
    </>
  );
}


// --- Final stage -----------------------------------------------------------

function FinalStage() {
  const bracket = useQuery({ queryKey: ['bracket', 'me'], queryFn: () => api.bracketMe() });
  const groups = useQuery({ queryKey: ['wc', 'groups'], queryFn: () => api.wcGroups() });
  const qc = useQueryClient();
  const nav = useNavigate();

  const [champion, setChampion] = useState<number | null>(null);
  const [goldenBoot, setGoldenBoot] = useState<string>('');

  useEffect(() => {
    if (bracket.data?.entry) {
      setChampion(bracket.data.entry.championTeamId);
      setGoldenBoot(
        bracket.data.entry.goldenBootPlayerId ? String(bracket.data.entry.goldenBootPlayerId) : '',
      );
    }
  }, [bracket.data]);

  const allTeams = useMemo(() => {
    const all: { id: number; name: string }[] = [];
    for (const g of groups.data?.groups ?? []) {
      for (const t of g.teams) all.push({ id: t.teamId, name: t.name });
    }
    return all.sort((a, b) => a.name.localeCompare(b.name));
  }, [groups.data]);

  const submit = useMutation({
    mutationFn: () =>
      api.bracketSubmitPicks({
        championTeamId: champion,
        goldenBootPlayerId: goldenBoot ? Number.parseInt(goldenBoot, 10) : null,
      }),
    onSuccess: async () => {
      haptic.success();
      await qc.invalidateQueries({ queryKey: ['bracket', 'me'] });
      nav('/bracket/confirmed');
    },
    onError: () => haptic.error(),
  });

  return (
    <div className="space-y-4">
      <section>
        <h2 className="font-display text-xl uppercase tracking-wide mb-2">Pick the champion</h2>
        <div className="space-y-1.5 max-h-96 overflow-y-auto rounded-xl border border-border-base bg-bg-card p-2">
          {allTeams.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                haptic.select();
                setChampion(t.id);
              }}
              className={cn(
                'w-full rounded-md px-3 py-2 text-left font-semibold transition-colors',
                champion === t.id ? 'bg-trophy-gold text-bg-night' : 'bg-bg-elevated text-text-base',
              )}
            >
              {t.name}
            </button>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl uppercase tracking-wide mb-2">Golden Boot</h2>
        <Card>
          <div className="text-xs text-text-dim mb-2">
            Pick the player you think finishes the tournament with the most goals.
          </div>
          <GoalscorerSearch value={goldenBoot} onChange={(id) => setGoldenBoot(id)} />
          {/* legacy input kept hidden as backup until first WC top-scorer ingest lands */}
          <input
            type="number"
            placeholder="Player ID"
            value={goldenBoot}
            onChange={(e) => setGoldenBoot(e.target.value)}
            className="hidden w-full rounded-lg bg-bg-night border border-border-base px-3 py-2 text-text-base outline-none focus:border-trophy-gold"
          />
        </Card>
      </section>

      <Button
        variant="gold"
        fullWidth
        disabled={!champion || submit.isPending}
        onClick={() => submit.mutate()}
      >
        {submit.isPending ? 'Locking…' : 'Lock it in 🔒'}
      </Button>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-2 animate-pulse">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="h-14 rounded-xl bg-bg-card border border-border-base" />
      ))}
    </div>
  );
}
